import { MED_SKU } from "src/utils/medEligibility";

import {
  hasExpressedInterestInNonOralsMedication,
  isMicrodosePaidTraffic,
  isOralsMedPaidTraffic,
  shouldRecommendCompoundedMicrodosePlan,
  shouldRecommendWegovyPillPlan,
  shouldRecommendTirzepatidePlan,
  isZepboundFeatureEnabled,
  shouldRecommendZepbound,
  isWegovyPillFeatureEnabled,
  isHealthlineBrandedMedsTraffic,
  isTirzepatidePaidTraffic,
  isTelex282Active,
} from "src/utils/userSegment/features";
import { getSurveyAnswers } from "src/hooks/survey/answers";
import { TreatmentOptions } from "@components/mht/utils/TreatmentOptions";
import {
  BrowserSessionState,
  updateSessionState,
} from "src/pageDefinitions/session";
import getStore from "src/utils/redux/store";
import { setCheckoutSaleItem } from "src/utils/redux/slices/checkoutOrders";

function isProductEligible(eligibleProducts: MED_SKU[], sku: MED_SKU): boolean {
  return eligibleProducts.includes(sku);
}

interface PlanRecommendationRule {
  sku: MED_SKU;
  condition: (eligibleProducts: MED_SKU[]) => boolean;
  category: "paid-traffic" | "regular-traffic";
}

const PLAN_RECOMMENDATION_CONFIG: PlanRecommendationRule[] = [
  {
    sku: MED_SKU.BRANDED,
    category: "paid-traffic",
    condition: (eligibleProducts) =>
      isHealthlineBrandedMedsTraffic() &&
      isProductEligible(eligibleProducts, MED_SKU.BRANDED),
  },
  {
    sku: MED_SKU.BRANDED_ZEPBOUND_VIAL,
    category: "paid-traffic",
    condition: (eligibleProducts) =>
      isProductEligible(eligibleProducts, MED_SKU.BRANDED_ZEPBOUND_VIAL) &&
      isZepboundFeatureEnabled() &&
      shouldRecommendZepbound(),
  },
  {
    sku: MED_SKU.BRANDED_WEGOVY_PILL,
    category: "paid-traffic",
    condition: (eligibleProducts) =>
      isProductEligible(eligibleProducts, MED_SKU.BRANDED_WEGOVY_PILL) &&
      isWegovyPillFeatureEnabled() &&
      shouldRecommendWegovyPillPlan(),
  },
  {
    sku: MED_SKU.COMPOUNDED_TIRZEPATIDE,
    category: "paid-traffic",
    condition: (eligibleProducts) =>
      isProductEligible(eligibleProducts, MED_SKU.COMPOUNDED_TIRZEPATIDE) &&
      isTirzepatidePaidTraffic() &&
      shouldRecommendTirzepatidePlan(),
  },
  // Microdose rule for paid traffic - routes users who select Microdose or Recommendation
  // via either Wegovy pill flow or Microdose paid traffic
  {
    sku: MED_SKU.COMPOUNDED_MICRODOSE,
    category: "paid-traffic",
    condition: (eligibleProducts) =>
      isProductEligible(eligibleProducts, MED_SKU.COMPOUNDED_MICRODOSE) &&
      (isWegovyPillFeatureEnabled() || isMicrodosePaidTraffic()) &&
      shouldRecommendCompoundedMicrodosePlan(),
  },
  {
    sku: MED_SKU.ORALS_METFORMIN,
    category: "paid-traffic",
    condition: (eligibleProducts) =>
      isProductEligible(eligibleProducts, MED_SKU.ORALS_METFORMIN) &&
      isOralsMedPaidTraffic() &&
      !hasExpressedInterestInNonOralsMedication(),
  },
  {
    sku: MED_SKU.COMPOUNDED_TIRZEPATIDE,
    category: "regular-traffic",
    condition: (eligibleProducts) =>
      isProductEligible(eligibleProducts, MED_SKU.COMPOUNDED_TIRZEPATIDE) &&
      isTelex282Active() &&
      shouldRecommendTirzepatidePlan(),
  },
  {
    sku: MED_SKU.COMPOUNDED,
    category: "regular-traffic",
    condition: (eligibleProducts) =>
      isProductEligible(eligibleProducts, MED_SKU.COMPOUNDED),
  },
  {
    sku: MED_SKU.GENERIC_LIRAGLUTIDE,
    category: "regular-traffic",
    condition: (eligibleProducts) =>
      isProductEligible(eligibleProducts, MED_SKU.GENERIC_LIRAGLUTIDE),
  },
  {
    sku: MED_SKU.COMPOUNDED_MICRODOSE,
    category: "regular-traffic",
    condition: (eligibleProducts) =>
      isProductEligible(eligibleProducts, MED_SKU.COMPOUNDED_MICRODOSE),
  },
  {
    sku: MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER,
    category: "regular-traffic",
    condition: (eligibleProducts) =>
      isProductEligible(
        eligibleProducts,
        MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER
      ),
  },
  {
    sku: MED_SKU.ORALS_METFORMIN,
    category: "regular-traffic",
    condition: (eligibleProducts) =>
      isProductEligible(eligibleProducts, MED_SKU.ORALS_METFORMIN),
  },
  {
    sku: MED_SKU.BRANDED,
    category: "regular-traffic",
    condition: (eligibleProducts) =>
      isProductEligible(eligibleProducts, MED_SKU.BRANDED),
  },
];

export function getMedRecommendedPlanTier(
  eligibleMedProducts: Array<MED_SKU>
): MED_SKU | undefined {
  for (const rule of PLAN_RECOMMENDATION_CONFIG) {
    if (rule.condition(eligibleMedProducts)) {
      return rule.sku;
    }
  }

  return undefined;
}

const HRT_TREATMENTS_MAP = {
  [TreatmentOptions.HORMONAL_CREAM_OR_GEL]: MED_SKU.HRT_CREAM,
  [TreatmentOptions.ESTROGEN_PATCH]: MED_SKU.HRT_PATCH,
};

export function getHRTRecommendedPlanTier(): MED_SKU | undefined {
  const {
    takingHRT,
    previousHRTTreatment,
    previousHRTPreference,
    currentHRTTreatment,
    currentHRTPreference,
    previouslyTakenHRT,
  } = getSurveyAnswers();

  let recommendedPlan = MED_SKU.HRT_CREAM;

  if (
    takingHRT?.includes("noNeverBefore") ||
    (takingHRT?.includes("no") && previouslyTakenHRT?.includes("no"))
  ) {
    return recommendedPlan;
  }

  const treatment = takingHRT?.includes("yes")
    ? currentHRTTreatment
    : previousHRTTreatment;
  const preference = takingHRT?.includes("yes")
    ? currentHRTPreference
    : previousHRTPreference;

  const usedTreatments =
    treatment &&
    Object.keys(HRT_TREATMENTS_MAP).filter((treatmentOption) =>
      treatment.includes(treatmentOption)
    );

  if (usedTreatments && usedTreatments.length === 1) {
    const oppositeTreatment = Object.keys(HRT_TREATMENTS_MAP).find(
      (key) => key !== usedTreatments[0]
    );

    recommendedPlan = preference.includes("yes")
      ? HRT_TREATMENTS_MAP[usedTreatments[0]]
      : HRT_TREATMENTS_MAP[oppositeTreatment];
  }

  return recommendedPlan;
}

export function setSelectedMedPlanBySKU(sku: MED_SKU) {
  const mapping: Record<MED_SKU, BrowserSessionState["selectedMedPlanType"]> = {
    [MED_SKU.BRANDED]: "branded",
    [MED_SKU.BRANDED_ZEPBOUND_VIAL]: "brandedZepboundVial",
    [MED_SKU.BRANDED_WEGOVY_PILL]: "brandedWegovyPill",
    [MED_SKU.COMPOUNDED]: "compounded",
    [MED_SKU.COMPOUNDED_TIRZEPATIDE]: "compoundedTirzepatide",
    [MED_SKU.GENERIC_LIRAGLUTIDE]: "genericLiraglutide",
    [MED_SKU.COMPOUNDED_MICRODOSE]: "compoundedMicrodose",
    [MED_SKU.ORALS_METFORMIN]: "oralsMetformin",
    [MED_SKU.HRT_CREAM]: undefined,
    [MED_SKU.HRT_PATCH]: undefined,
    [MED_SKU.COMPOUNDED_MICRODOSE_PROACTIVE_HEALTH_BIOMARKER]:
      "compoundedMicrodoseProactiveHealthBiomarker",
  };
  const { eligibleSaleItems } = getStore().getState().recommendationContext;
  const eligibleSaleItem = eligibleSaleItems?.find(
    (medPlanId) => medPlanId.medSku === sku
  );
  if (eligibleSaleItem) {
    getStore().dispatch(setCheckoutSaleItem(eligibleSaleItem));
  }
  updateSessionState("browser", {
    selectedMedPlanType: mapping[sku],
  });
  updateSessionState("browser", {
    selectedNoomWeightPlanOption: false,
  });
}
